// Routes and tracks with unsaved changes, as the API's JSON so Save can send it back. New ones (read from a file
// dropped on the map, or drawn) open under a local id starting "new-"; a saved route being edited keeps its own
// id. They live in memory, and draftStore keeps a copy in the browser so a crash or reload doesn't lose them.
import type { ApiTrail, TrailKind } from './trail';

export interface Draft {
  kind: TrailKind;
  json: ApiTrail;
}

const DRAFT_PREFIX = 'new-';
const drafts = new Map<string, Draft>();
const listeners = new Set<() => void>();
const changed = () => {
  for (const listener of listeners) listener();
};

/** Called after any draft is added, changed or removed. */
export function subscribeDrafts(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const isDraft = (guid: string) => guid.startsWith(DRAFT_PREFIX);

export function addDraft(kind: TrailKind, json: ApiTrail): string {
  const guid = `${DRAFT_PREFIX}${crypto.randomUUID()}`;
  drafts.set(guid, { kind, json });
  changed();
  return guid;
}

export const getDraft = (guid: string) => drafts.get(guid);

/** Holds changes to a trail (new or saved) until it is saved. */
export function setDraft(guid: string, kind: TrailKind, json: ApiTrail) {
  drafts.set(guid, { kind, json });
  changed();
}

/** Every draft, by id (for keeping a copy). */
export const allDrafts = (): [string, Draft][] => [...drafts];

/** Whether a trail has changes that aren't saved. */
export const hasDraft = (guid: string) => drafts.has(guid);

export function removeDraft(guid: string) {
  if (drafts.delete(guid)) changed();
}

/** Whether a trail has unsaved changes worth warning about (a new route with no points yet isn't). */
export function hasUnsavedChanges(guid: string): boolean {
  const json = drafts.get(guid)?.json;
  return !!json && ((json.points?.length ?? 0) > 0 || (json.segments?.length ?? 0) > 0 || !isDraft(guid));
}
